import type { ReactNode } from 'react';

import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { useAtom } from 'jotai';

import { useMatchMediaQuery } from '@twistopayments/design-system/hooks';
import { getTheme } from '@twistopayments/design-system/theme';
import type { Brand, ThemeConfig, ThemeModes } from '@twistopayments/design-system/theme';
import { atomWithLocalStorage } from '@twistopayments/design-system/utils';

import { thmemeChangeAnimation } from './theme-change-animation';

type Props = {
  config?: ThemeConfig;
  /** Brand preset selecting the base palette ('b2c' default | 'b2b'). */
  brand?: Brand;
  children: ReactNode;
};

export type ThemeModesWithAuto = ThemeModes | 'auto';

const defaultValue = 'auto';

export const themeModeAtom = atomWithLocalStorage('themeMode', defaultValue);

export const ThemeProvider = ({ config, brand, children }: Props) => {
  const [themeMode] = useAtom(themeModeAtom);

  const isDarkModePreferred = useMatchMediaQuery(
    '(prefers-color-scheme: dark)',
    thmemeChangeAnimation,
  );
  const detectedThemeMode =
    themeMode === 'auto' ? (isDarkModePreferred ? 'dark' : 'light') : (themeMode as ThemeModes);

  if (typeof window !== 'undefined') {
    window.document.documentElement.setAttribute('data-theme', detectedThemeMode);
  }

  const theme = getTheme({ mode: detectedThemeMode, brand, config });

  return <EmotionThemeProvider theme={theme}>{children}</EmotionThemeProvider>;
};
